import { useEffect, useState } from 'react'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { ChevronLeft, Clipboard } from 'lucide-react'
import { toast } from 'react-toastify'
import styled from 'styled-components/macro'

import apiService from 'services/api'
import { useCompanyOrDoorData } from 'hooks/useCompanyOrDoorData'
import { CreatableTagSelector } from 'components/CreatableTagSelector'
import { CreatableDistributorSelector } from 'components/CreatableDistributorSelector'
import { CreatableGPOSelector } from 'components/CreatableGPOSelector'
import { CreatableCampaignSelector } from 'components/CreatableCampaignSelector'
import FbButton from 'components/FbUI/FbButton'
import { Tag } from 'models/tags'
import { CompanyDistributor, CompanyGPO } from 'models/companies'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { ContactCompanyType } from 'models/contact_companies'
import { getIdWithPrefix } from 'utils/pathUtils'
import { BackButton } from 'components/Mobile/Mobile.styles'

const attributesSchema = z.object({
  taglist: z.any().optional().nullable(),
  distributors: z.any().optional().nullable(),
  gpos: z.any().optional().nullable(),
  campaigns: z.any().optional().nullable(),
})

export type MobileAttributesInput = z.infer<typeof attributesSchema>

export function MobileAccountAttributesPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const urlParams = useParams()
  const { preferences } = usePreferences()
  const queryClient = useQueryClient()
  const api = apiService()

  // Get chainProxyId from location state or URL params
  const stateData = location.state as {
    chainProxyId?: number
    chainName?: string
  } | null

  // Check if we're on a door page (restaurant/:door_id path)
  const doorId = urlParams?.door_id

  // Fetch door data if we have a door_id
  const { data: doorData } = api.useGetPlaceV2(doorId)
  const { data: doorProxyData } = api.useGetDoorProxy(
    doorData?.id ? doorData.id : undefined
  )

  // Parse chain ID from URL if not in state
  const chainID = urlParams?.chain_name
    ? getIdWithPrefix(
        urlParams?.chain_name,
        preferences?.company_type_prefix_map[ContactCompanyType.Chain]
      )
    : undefined

  // Fetch chain data if we only have chainID
  const { data: chainData } = api.useGetChain(chainID)

  // Determine the contact company ID to use
  // For doors, use the door proxy's chain_proxy (which is the parent chain proxy ID)
  // For chains, use chainProxyId from state or chainData
  const chainProxyId =
    doorProxyData?.chain_proxy ||
    stateData?.chainProxyId ||
    chainData?.contact_company_id

  // Fetch options for selectors
  const { data: distributorsOptions } = api.useGetCompanyDistributorsOptions()
  const { data: gposOptions } = api.useGetCompanyGPOsOptions()
  const { data: tagsOptions } = api.useGetTagsOptions()
  const { data: campaignsOptions } =
    api.useGetCampaignsOptions('restaurants-bars')

  // Fetch company or door data
  // Pass doorProxyData?.id to fetch door-specific data when on door page
  const { data: effectiveData, refetch: refetchData } = useCompanyOrDoorData(
    chainProxyId || 0,
    doorProxyData?.id
  )

  const [previousDistributors, setPreviousDistributors] = useState<number[]>([])
  const [previousGPOs, setPreviousGPOs] = useState<number[]>([])
  const [previousTaglist, setPreviousTaglist] = useState<number[]>([])
  const [previousCampaigns, setPreviousCampaigns] = useState<number[]>([])

  const { control, handleSubmit, reset, setError, formState } =
    useForm<MobileAttributesInput>({
      defaultValues: {
        taglist: undefined,
        distributors: undefined,
        gpos: undefined,
        campaigns: undefined,
      },
      resolver: zodResolver(attributesSchema),
    })

  // Initialize form with company data
  useEffect(() => {
    if (effectiveData) {
      const taglist: Tag[] = effectiveData.taglist || []
      const distributors: CompanyDistributor[] =
        effectiveData.distributors || []
      const gpos: CompanyGPO[] = effectiveData.gpos || []
      const campaigns: any[] = effectiveData.campaigns || []

      const mappedTaglist = taglist.map((t) => t.id) || []
      const mappedDistributors = distributors.map((d) => d.id) || []
      const mappedGPOs = gpos.map((g) => g.id) || []
      const mappedCampaigns = campaigns.map((c) => c.id) || []

      reset({
        taglist: mappedTaglist,
        distributors: mappedDistributors,
        gpos: mappedGPOs,
        campaigns: mappedCampaigns,
      })

      setPreviousDistributors(mappedDistributors)
      setPreviousGPOs(mappedGPOs)
      setPreviousTaglist(mappedTaglist)
      setPreviousCampaigns(mappedCampaigns)
    }
  }, [effectiveData, reset])

  // Determine if we're editing a door or a company
  const isDoorPage = !!doorProxyData?.id

  const mutation = useMutation({
    mutationFn: async (values: MobileAttributesInput) => {
      if (isDoorPage && doorProxyData?.id) {
        // Use door-specific bulk edit API (note: doesn't support campaigns)
        await api.bulkEditDoorAttrs({
          door_proxy_ids: [doorProxyData.id],
          taglist:
            Array.isArray(values.taglist) && values.taglist.length > 0
              ? values.taglist
              : 'REMOVE',
          distributors:
            Array.isArray(values.distributors) && values.distributors.length > 0
              ? values.distributors
              : 'REMOVE',
          gpos:
            Array.isArray(values.gpos) && values.gpos.length > 0
              ? values.gpos
              : 'REMOVE',
          previous_distributors: previousDistributors,
          previous_gpos: previousGPOs,
          previous_taglist: previousTaglist,
        })
      } else if (chainProxyId) {
        // Use company bulk edit API
        await api.bulkEditCompanyAttrs({
          contact_company_ids: [chainProxyId],
          taglist:
            Array.isArray(values.taglist) && values.taglist.length > 0
              ? values.taglist
              : 'REMOVE',
          distributors:
            Array.isArray(values.distributors) && values.distributors.length > 0
              ? values.distributors
              : 'REMOVE',
          gpos:
            Array.isArray(values.gpos) && values.gpos.length > 0
              ? values.gpos
              : 'REMOVE',
          campaigns:
            Array.isArray(values.campaigns) && values.campaigns.length > 0
              ? values.campaigns
              : 'REMOVE',
          previous_distributors: previousDistributors,
          previous_gpos: previousGPOs,
          previous_taglist: previousTaglist,
          previous_campaigns: previousCampaigns,
        })
      }
    },
    onSettled: async (_, err) => {
      refetchData()
      queryClient.invalidateQueries({ queryKey: ['notes-table'] })
      queryClient.invalidateQueries({
        queryKey: ['companyActivity', chainProxyId],
      })
      if (isDoorPage && doorProxyData?.id) {
        queryClient.invalidateQueries({
          queryKey: ['door_proxy', doorProxyData.id],
        })
      }
      if (err) {
        toast.error('Error updating attributes.')
      } else {
        toast.success('Attributes updated successfully.')
        reset(formState.defaultValues)
      }
    },
  })

  const onSubmit = handleSubmit(async (values) => {
    mutation.mutate(values)
  })

  const handleBack = () => {
    navigate(-1)
  }

  return (
    <PageContainer>
      {/* Back button header */}
      <BackButton onClick={handleBack}>
        <ChevronLeft size={20} />
        <span>Back</span>
      </BackButton>

      {/* Content */}
      <Content>
        {/* Title */}
        <TitleSection>
          <Clipboard size={18} className="text-gray-500" />
          <span className="text-sm font-semibold tracking-wide uppercase">
            ACCOUNT ATTRIBUTES
          </span>
        </TitleSection>

        {/* Form */}
        <FormSection>
          {/* Tags */}
          <CreatableTagSelector
            name="taglist"
            control={control}
            label="Tags"
            placeholder="Tags"
            options={tagsOptions}
            setError={setError}
            labelClassName="font-inter font-normal text-sm text-gray-700 mb-2"
            whiteBackground
          />

          {/* Campaigns - only shown for company pages, not door pages */}
          {!isDoorPage && (
            <CreatableCampaignSelector
              name="campaigns"
              control={control}
              label="Campaigns"
              placeholder="Campaign(s)"
              options={campaignsOptions}
              setError={setError}
              labelClassName="font-inter font-normal text-sm text-gray-700 mb-2"
              whiteBackground
              companyType="restaurants-bars"
              isClearable={false}
            />
          )}

          {/* Distributors */}
          <CreatableDistributorSelector
            name="distributors"
            control={control}
            label="Distributors"
            placeholder="Distributor(s)"
            options={distributorsOptions}
            setError={setError}
            labelClassName="font-inter font-normal text-sm text-gray-700 mb-2"
            whiteBackground
          />

          {/* GPOs */}
          <CreatableGPOSelector
            name="gpos"
            control={control}
            label="Group Purchasing Organization (GPO)"
            placeholder="GPO(s)"
            options={gposOptions}
            setError={setError}
            labelClassName="font-inter font-normal text-sm text-gray-700 mb-2"
            whiteBackground
          />
        </FormSection>

        {/* Save Button - Always visible at bottom */}
        {formState.isDirty && (
          <SaveButtonContainer>
            <FbButton
              loading={mutation.isPending}
              onClick={onSubmit}
              className="w-full bg-[#2C6B4E] hover:bg-[#245A42] text-white"
            >
              Save
            </FbButton>
          </SaveButtonContainer>
        )}
      </Content>
    </PageContainer>
  )
}

// Styled components
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  background-color: #f9fafb;
`

const Content = styled.div`
  flex: 1;
  padding: 16px 16px 100px;
  overflow-y: auto;
`

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const SaveButtonContainer = styled.div`
  margin-top: 24px;
  padding-bottom: 16px;
`
